import { useEffect, useMemo, useRef, useState } from 'react';
import { registerShortcuts } from '../../hooks/shortcutRegistry';
import {
  Dialog, DialogTitle, DialogContent, Box, Typography, IconButton, Button, Tooltip,
  MenuItem, FormControl, RadioGroup, Radio, FormControlLabel, Checkbox,
  CircularProgress, Menu, ListItemIcon, ListItemText, Divider,
  useMediaQuery, Drawer,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DialogActionsBar from '../../components/shared/DialogActionsBar';
import Badge from '@mui/material/Badge';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CheckIcon from '@mui/icons-material/Check';
import MenuIcon from '@mui/icons-material/Menu';
import DateField from '../../components/form/DateField';
import { useGlobalStore } from '../../stores/globalStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { useTrayTicketData, useTrayTicketMenuCycle, useTrayTicketPatientList, useTrayTicketColorConfig, trayTicketKeys, fetchTrayTicketMenuCycle } from '../../api/trayTicketApi';
import { useQueryClient } from '@tanstack/react-query';
import { API } from '../../api/endpoints';
import { argbToHex } from '../../types/facility';
import { useQueueReport } from '../../api/reportsApi';
import { useReportAutoOpener } from '../reports/useReportAutoOpener';
import type { TrayTicketPatientListItem, TrayTicketRequest } from '../../types/trayTicket';
import TrayTicketVisualizerContent from './components/TrayTicketVisualizerContent';
import TrayTicketNoticesPanel, { countRealNotices } from './components/TrayTicketNoticesPanel';
import { TTV_MEDIA } from './trayTicketVisualizerLayout';
import type { TrayTicketVisualizerSelection } from './trayTicketVisualizerUrl';
import TrayTicketExplainer from './TrayTicketExplainer';
import SelectField from '../../components/form/SelectField';
import './TrayTicketVisualizerDialog.css';
import { TTV_MEALTIME_CHIPS } from './trayTicketMealtimeChips';
import { useApiImage } from '../../hooks/useApiImage';
import { reportQueuedToast } from '../../utils/reportQueuedToast';
import NumberField from '../../components/form/NumberField';

/**
 * Standalone modal implementation of the WinForms
 * `frmDietech_Prd_TrayTicketVisualizer` compact ribbon UI.
 *
 * Reuses the existing `TrayTicketVisualizerContent` inner renderer
 * for the tray body. Toolbar mirrors WinForms: Print / Refresh /
 * Explain Ticket / Resident dropdown / Menu Cycle date / Mealtime
 * radios. (The former `/menucycle/trayticket` page was removed;
 * this dialog is the only tray-ticket visualizer entry point.)
 *
 * Callers pass `initialPatientId` / `initialDate` / `initialMealtime`
 * to preselect state (e.g. from the residents grid). All local
 * afterwards — no URL sync.
 */

/** Hardcoded default names used only when a facility hasn't
 *  customized the slot AND that slot is the currently-selected
 *  mealtime (matches WinForms `Fill_Mealtime` placeholder branch
 *  in `frmDietech_Prd_TrayTicketVisualizer.vb:252-274` — the "**"
 *  suffix flags it as a facility-unnamed default). */
const MEALTIME_DEFAULTS: readonly string[] = [
  'Breakfast', 'Dinner', 'Supper',   // 0/1/2  (WinForms uses UK-style names for the mask)
  'EAM', 'AM', 'PM', 'HS', 'PM2', 'HS2', // 3-8 nourishments
];

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** Preselect a patient by PKey when the dialog opens. */
  initialPatientId?: number | null;
  /** Preselect the ticket date (YYYY-MM-DD). Defaults to today. */
  initialDate?: string;
  /** Preselect mealtime (0 = Breakfast, 1 = Lunch, 2 = Dinner). Defaults to 0. */
  initialMealtime?: number;
  /**
   * Fired whenever the resident / date / mealtime selected *inside*
   * the dialog changes (and once on open with the seeded values).
   *
   * The dialog owns a resident dropdown of its own, so without this
   * the caller and the dialog drift: flip to another resident here
   * and the grid row behind still highlights the one you launched
   * from, which on a clinical screen is a genuine "whose ticket did
   * I just read" hazard. Grid callers use it to move the focused
   * row; every caller uses it to keep the URL honest.
   */
  onSelectionChange?: (selection: TrayTicketVisualizerSelection) => void;
}

/**
 * The ticket sheet: a white page with a soft shadow, centred on a neutral
 * canvas, the way a PDF preview presents a document.
 *
 * The point is to separate the thing being previewed from the tool previewing
 * it. Before this, the ticket bled edge-to-edge into the dialog's own white
 * background, so the Times New Roman print body and the sans-serif app chrome
 * sat on one undifferentiated plane and the ticket read as "the screen" rather
 * than "the page that will come out of the printer".
 *
 * Nothing inside the sheet changed — the print body, the grey mealtime band,
 * the red ISOLATION / "Selective Menu Not Entered" text are all untouched, and
 * the TrayTicketVisualizerContent snapshot proves it.
 */
const TTV_SHEET = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  width: '100%',
  maxWidth: 1040,
  mx: 'auto',
  bgcolor: 'background.paper',
} as const;


/**
 * The resident dropdown (WinForms `beRibbonGluResident`): hosts A to Z with
 * their guests indented under them, each row the name over its diet order.
 * Typing narrows on either, as the Find box in the old raw Select did.
 */
function ResidentPicker({
  patients, value, onChange, loading, disabled, residentLabel, residentPlural, width,
}: {
  patients: readonly TrayTicketPatientListItem[];
  value: number | null;
  onChange: (id: number | null) => void;
  loading: boolean;
  disabled: boolean;
  residentLabel: string;
  residentPlural: string;
  width: number | string;
}) {
  return (
    <SelectField<TrayTicketPatientListItem>
      // Named for what it chooses, not a bare "combobox".
      ariaLabel={`Select ${residentLabel}`}
      dataSource={patients}
      displayExpr="fullName"
      valueExpr="fKey_Patient"
      searchExpr={['fullName', 'dietOrderText']}
      value={value}
      onValueChanged={(v) => onChange(v == null ? null : Number(v) || null)}
      placeholder={loading ? `Loading ${residentPlural}...` : `Select ${residentLabel}`}
      disabled={disabled}
      showClearButton={false}
      maxDropdownHeight={480}
      noDataText="No matches"
      width={width}
      itemRender={(p) => (
        <span className="ttv-resident-option" style={{ display: 'flex', flexDirection: 'column', minWidth: 0, paddingLeft: p.host_FKey_Patient != null ? 16 : 0 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {p.host_FKey_Patient != null && <i className="bi bi-people-fill" aria-hidden="true" style={{ fontSize: 12, color: 'var(--brand-accent)' }} />}
            <span>{p.fullName}</span>
          </span>
          {p.dietOrderText && (
            <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-small)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.dietOrderText}
            </span>
          )}
        </span>
      )}
    />
  );
}

/** The resident photo, fetched with the API headers; renders nothing until it loads or when there is none. */
function ResidentPhoto({ path, alt, style }: { path: string; alt: string; style: React.CSSProperties }) {
  const src = useApiImage(path);
  return src ? <img src={src} alt={alt} style={style} /> : null;
}

export default function TrayTicketVisualizerDialog({
  open, onClose, initialPatientId, initialDate, initialMealtime, onSelectionChange,
}: Props) {
  // Three stages, not one cliff — see `trayTicketVisualizerLayout.ts`
  // for why and for the widths. `isNarrow` keeps its old meaning (body
  // stacks, mobile summary strip appears); `isDrawer` is the new, later
  // point at which the ribbon actually folds away, so a half-screen
  // 1000px window keeps a real toolbar instead of dropping to the
  // phone layout.
  const isNarrow = useMediaQuery(TTV_MEDIA.narrow);
  const isDrawer = useMediaQuery(TTV_MEDIA.drawer);
  const isFullScreen = useMediaQuery(TTV_MEDIA.fullScreen);
  const facilityId = useGlobalStore((s) => s.facilityId);
  const userId = useGlobalStore((s) => s.userId);
  const facilitySettings = useGlobalStore((s) => s.facilitySettings);
  // Facility-configurable labels — the customer may rename
  // "Resident" (e.g. "Patient", "Client"). Every user-facing string
  // in this dialog runs through these two variables.
  const residentLabel = useGlobalStore((s) => s.residentLabel);
  const residentPluralLabel = useGlobalStore((s) => s.residentPluralLabel);
  const residentLabelLower = residentLabel.toLowerCase();
  const residentPluralLower = residentPluralLabel.toLowerCase();
  const addNotification = useNotificationStore((s) => s.addNotification);

  // Toolbar state
  const [selectedDate, setSelectedDate] = useState<string>(initialDate ?? todayStr());
  const [selectedMealtime, setSelectedMealtime] = useState<number>(initialMealtime ?? 0);
  const [selectedPatientId, setSelectedPatientId] = useState<number | null>(initialPatientId ?? null);
  const [explainerOpen, setExplainerOpen] = useState(false);

  const queryClient = useQueryClient();

  // Report the live selection to the caller. Kept in a ref so a
  // caller passing an inline arrow doesn't re-fire this on every
  // parent render — it must fire on selection changes, nothing else.
  const onSelectionChangeRef = useRef(onSelectionChange);
  useEffect(() => { onSelectionChangeRef.current = onSelectionChange; });
  useEffect(() => {
    if (!open) return;
    onSelectionChangeRef.current?.({
      patientId: selectedPatientId,
      date: selectedDate,
      mealtime: selectedMealtime,
    });
  }, [open, selectedPatientId, selectedDate, selectedMealtime]);

  // Clear local state on close, and drop the two caches that must
  // never be served from memory on the next open.
  //
  // This used to `removeQueries` the whole `trayTicket` root, which
  // meant every reopen started cold — including the resident dropdown
  // and the menu-cycle header. That is precisely the wrong thing to
  // throw away, because the dominant workflow is open → read → close
  // → next resident, over and over through a meal service.
  //
  // What still gets dropped:
  //   • `data` — the generated ticket itself. Regenerated per open so
  //     a diet order changed since the last look can never be shown
  //     from cache. Clinical correctness beats the millisecond.
  //   • `explainer` — derived from that same generation run, so it
  //     would go stale in lockstep with it.
  //
  // What now survives: `menuCycle`, `patientList`, `displayOptions`,
  // `guestSchedule` — facility-shaped lookups keyed by date/mealtime
  // that don't change mid-service, each with a staleTime in
  // `trayTicketApi.ts` bounding how long a reopen may trust them.
  useEffect(() => {
    if (open) return;
    setSelectedDate(todayStr());
    setSelectedMealtime(0);
    setSelectedPatientId(null);
    setExplainerOpen(false);
    setMobileDrawerOpen(false);
    setPrintMenuAnchor(null);
    queryClient.removeQueries({ queryKey: [...trayTicketKeys.all, 'data'] });
    queryClient.removeQueries({ queryKey: [...trayTicketKeys.all, 'explainer'] });
  }, [open, queryClient]);

  // Debounced toolbar inputs — every query that keys off
  // date/mealtime/patient reads the DEBOUNCED scalars so a
  // rapid string of user clicks doesn't spam the server. Timer
  // restarts on each raw-input change; the UI stays showing the
  // previously-committed values until 2000ms of quiet has passed.
  // Additionally: if the patient dropdown OR the date-edit popup
  // is OPEN, the timer is suspended — nothing generates until
  // the user picks a value (or dismisses the popup) and 2000ms
  // of quiet passes.
  const [debouncedDate, setDebouncedDate] = useState<string>(selectedDate);
  const [debouncedMealtime, setDebouncedMealtime] = useState<number>(selectedMealtime);
  const [debouncedPatientId, setDebouncedPatientId] = useState<number | null>(selectedPatientId);
  const [dateEditOpen, setDateEditOpen] = useState(false);

  // Reseed on open so the caller's props take effect for each launch.
  //
  // Done as a render-phase transition rather than an effect, for
  // three reasons.
  //
  // Correctness: `onSelectionChange` writes the caller's URL, and an
  // effect-based reseed would let the reporting effect fire once with
  // the PREVIOUS launch's values before the new seed landed —
  // publishing a wrong resident to the address bar for a frame.
  // Adjusting state during render re-renders before effects run, so
  // everything downstream observes the seeded values and only those.
  //
  // Stability: it fires only on the false→true edge, so a caller
  // re-rendering with a new `initialPatientId` while the dialog is
  // already open (which URL sync now makes routine) no longer yanks
  // the user's in-dialog selection out from under them.
  //
  // Speed: the debounced scalars below are seeded in the SAME pass.
  // The 2000ms debounce exists to stop a rapid string of toolbar
  // clicks from spamming the server — it was never meant to apply to
  // the values the dialog opens with, but it did, so every launch sat
  // on a skeleton for two full seconds before the ticket fetch was
  // even issued. Committing the seed directly means the open is a
  // debounced *change* of nothing, and the queries fire immediately.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      const seedDate = initialDate ?? todayStr();
      const seedMealtime = initialMealtime ?? 0;
      const seedPatientId = initialPatientId ?? null;
      setSelectedDate(seedDate);
      setSelectedMealtime(seedMealtime);
      setSelectedPatientId(seedPatientId);
      setDebouncedDate(seedDate);
      setDebouncedMealtime(seedMealtime);
      setDebouncedPatientId(seedPatientId);
      setExplainerOpen(false);
      }
  }

  useEffect(() => {
    // Suspend the debounce timer while the date popup is open. When
    // it closes (`dateEditOpen` flips to false) this effect re-runs
    // and the timer starts fresh. The resident dropdown needs no
    // gate: its value changes only on a pick, which closes it.
    if (dateEditOpen) return;
    const t = setTimeout(() => {
      setDebouncedDate(selectedDate);
      setDebouncedMealtime(selectedMealtime);
      setDebouncedPatientId(selectedPatientId);
    }, 2000);
    return () => clearTimeout(t);
  }, [selectedDate, selectedMealtime, selectedPatientId, dateEditOpen]);

  // Menu-cycle-only lookup. Reads the DEBOUNCED date so date clicks
  // don't spam this endpoint.
  const menuCycleQuery = useTrayTicketMenuCycle(facilityId, debouncedDate, open);
  // Bare-minimum patient list for the resident dropdown. Reads the
  // DEBOUNCED date + mealtime so rapid toolbar changes don't trigger
  // a refetch per click. This replaced a heavy full-data list call
  // (`useTrayTicketData` over every patient on the tray); the
  // dropdown only ever needed name + demographics, and everything
  // else is fetched per-resident by `detailQuery` on selection.
  const patientListQuery = useTrayTicketPatientList(facilityId, debouncedDate, debouncedMealtime, open);

  const debouncedDetailRequest = useMemo((): TrayTicketRequest => {
    if (!debouncedPatientId) return { date: '', mealtime: 0 };
    return {
      date: debouncedDate,
      mealtime: debouncedMealtime,
      fKey_Patient: debouncedPatientId,
      includeGuests: false,
    };
  }, [debouncedDate, debouncedMealtime, debouncedPatientId]);

  // Stage 3 (re-enabled): per-patient menu-generation call. Fires
  // when a patient is selected. Returns the full TrayTicketPatient
  // (diet order applied to date/mealtime, allergies, TT messages,
  // SF, selective-menu status, mat colors, tray notes, and menu
  // items). Options + post-processing on the server side
  // (`GenerateMenuItemsForPatients`) duplicate WinForms
  // `frmDietech_Prd_TrayTicketVisualizer.vb:2142-2170` exactly:
  //   • ProductionMenuGenerator with 6 flags set
  //   • CreateAllPatientMenus(6)
  //   • TT_ItemText==1 swap
  //   • EvaluateMultiPageTT(ttCount:1, itemCount:18)
  const detailQuery = useTrayTicketData(facilityId, open ? debouncedDetailRequest : { date: '', mealtime: 0 });

  // Initial-load gate — true only while the FIRST patient-list
  // query is in flight. Once the list returns (dropdown populated),
  // controls become interactive. Menu-item fetches (detailQuery)
  // don't lock the toolbar — they render as row shimmer inside
  // the tray body instead.
  const isInitialLoading = patientListQuery.isLoading;

  // Dropdown source — bare-minimum rows from the lightweight
  // patient-list endpoint. Full patient data (diet types,
  // allergies, etc.) is fetched on-demand via detailQuery when a
  // resident is selected.
  const patients = patientListQuery.data ?? [];
  // Basic patient row from the default-load list — has name +
  // demographics + diet order text. Enough to render the banner
  // and patient-info label immediately after the list returns.
  const selectedPatientBasic = selectedPatientId != null
    ? patients.find((p) => p.fKey_Patient === selectedPatientId) ?? null
    : null;
  // Full patient row from the detail call — has allergies, TT
  // messages, SF, selective-menu status, mat colors, tray notes,
  // and menu items. Preferred when available; falls back to the
  // basic row so banner + demographics keep rendering while the
  // detail call is in flight.
  const selectedPatientDetail = detailQuery.data?.patients?.[0] ?? null;
  const selectedPatient: any = selectedPatientDetail ?? selectedPatientBasic;
  // Detail-derived UI (diet banner, consistencies, SF, allergies,
  // mealtime banner, mat colors, TT messages, items) should hide
  // whenever the menu-generation call is in flight — including
  // Refresh clicks that refetch on cached data. Cache still holds
  // the previous rows so `selectedPatientDetail` is truthy during
  // a refetch; explicit `!isFetching` clears it.
  // Fresh = we have data AND the query isn't currently fetching.
  // Intentionally NOT gated on `detailRequestPending`: during
  // the 500ms debounce window the UI must stay stable showing
  // the previous data. Only once the timer elapses and the
  // debounced query key actually flips does `isFetching` go
  // true and the shimmer appear.
  const hasFreshDetail = !!selectedPatientDetail
    && !detailQuery.isFetching;
  // Extended disable gate — Print / Refresh / Explain + mealtime
  // radios stay disabled until the menu-generation call returns.
  // Patient dropdown + date picker unlock earlier (as soon as the
  // list returns) so users can pick a different patient / date.
  const isDetailLoading = !hasFreshDetail;
  const colorConfig = useTrayTicketColorConfig();

  // Sort hosts alphabetically; interleave each host's guests
  // immediately after (also alpha within a host). Mirrors WinForms
  // `Fill_luFKey_Patient` sort + guest indent in
  // `gvPatient_CustomDrawCell`.
  //
  // Guest detection: use `host_FKey_Patient != null` — the `isGuest`
  // bool is a convenience derived from the same field server-side,
  // but null-check is one step less error-prone if the API is out
  // of date with the DTO.
  const sortedPatients = useMemo(() => {
    const isG = (p: typeof patients[number]) => p.host_FKey_Patient != null;
    const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
    const hosts = patients.filter((p) => !isG(p));
    const guestsByHost = new Map<number, typeof patients>();
    for (const g of patients) {
      if (!isG(g) || g.host_FKey_Patient == null) continue;
      const arr = guestsByHost.get(g.host_FKey_Patient) ?? [];
      arr.push(g);
      guestsByHost.set(g.host_FKey_Patient, arr);
    }
    hosts.sort((a, b) => collator.compare(a.fullName, b.fullName));
    for (const arr of guestsByHost.values()) {
      arr.sort((a, b) => collator.compare(a.fullName, b.fullName));
    }
    const out: typeof patients = [];
    for (const h of hosts) {
      out.push(h);
      const gs = guestsByHost.get(h.fKey_Patient);
      if (gs) out.push(...gs);
    }
    // Orphan guests (host missing from list) — append at the end
    for (const g of patients) {
      if (!isG(g)) continue;
      if (out.includes(g)) continue;
      out.push(g);
    }
    return out;
  }, [patients]);

  // Mealtime radio list — mirrors WinForms `Fill_Mealtime`
  // (`frmDietech_Prd_TrayTicketVisualizer.vb:247-296`):
  //   • Lite facilities: slots 0-2 only.
  //   • Skip a slot entirely when the facility name is blank
  //     UNLESS the slot is the currently-selected mealtime — in
  //     that case render a hardcoded default with "**" suffix.
  //   • Otherwise use the facility's customized name.
  //   • Cap at 8 items to match the WinForms `mRepRad_Mealtimes`
  //     slot count.
  // TODO: attendance markers "(SM)" / "(NP)" per WinForms
  //   `SetPatientAttendance` (.vb:3070-3186) require the
  //   patient's `Attend_Day_{B|L|D}{dayOfWeek}` fields, which
  //   `TrayTicketPatient` doesn't expose today. Add to server
  //   DTO to enable.
  const facilityMealNames: readonly string[] = useMemo(() => {
    const fs = facilitySettings;
    return [
      fs?.break_Name ?? '',
      fs?.lunch_Name ?? '',
      fs?.dinner_Name ?? '',
      fs?.nourish_Name_EAM ?? '',
      fs?.nourish_Name_AM ?? '',
      fs?.nourish_Name_PM ?? '',
      fs?.nourish_Name_HS ?? '',
      fs?.nourish_Name_PM2 ?? '',
      fs?.nourish_Name_HS2 ?? '',
    ];
  }, [facilitySettings]);

  const isProduction = facilitySettings?.useProduction ?? false;

  const mealtimeOptions = useMemo(() => {
    // Step 1: seed base name list from facility settings, matching
    // WinForms `Fill_Mealtime` — skip blanks unless it's the current
    // selection, then swap in the `**` default. Attendance markers
    // are appended in step 2.
    const base: Array<{ value: number; label: string; attendance: string }> = [];
    for (let slot = 0; slot < 9; slot++) {
      if (!isProduction && slot >= 3) continue;
      const name = (facilityMealNames[slot] ?? '').trim();
      if (name === '') {
        if (slot !== selectedMealtime) continue;
        base.push({ value: slot, label: `${MEALTIME_DEFAULTS[slot]}**`, attendance: '' });
      } else {
        base.push({ value: slot, label: name, attendance: '' });
      }
      if (base.length >= 8) break;
    }

    // Step 2: attendance markers — mirrors WinForms
    // `SetPatientAttendance` (.vb:3070-3186). Production-only.
    // Cascade rules from each B/L/D main-meal code to its
    // nourishments:
    //   Attend=1 → main "(SM)"
    //   Attend=2 → main "(NP)" + BOTH its nourishments "(NP)"
    //   Attend=3 → main "(NP)" + FIRST nourishment "(NP)" (EAM/PM/HS)
    //   Attend=4 → main "(NP)" (EAM & AM only — no additional cascade)
    //   Attend=5 → main "(NP)" + EAM "(NP)" (AM only)
    //   Attend=6 → main "(NP)" (PM & PM2 only)
    //   Attend=7 → main "(NP)" + PM "(NP)" (PM2 only)
    //   Attend=8 → main "(NP)" (HS & HS2 only)
    //   Attend=9 → main "(NP)" + HS "(NP)" (HS2 only)
    // Nourishment slot mapping: B→EAM(3)/AM(4), L→PM(5)/PM2(7), D→HS(6)/HS2(8).
    if (isProduction && selectedPatient) {
      const attendCodes: readonly [number, number, number] = [
        selectedPatient.attend_B ?? 0,
        selectedPatient.attend_L ?? 0,
        selectedPatient.attend_D ?? 0,
      ];
      const nourishmentsForMain: readonly [number, number][] = [
        [3, 4], // B → EAM, AM
        [5, 7], // L → PM, PM2
        [6, 8], // D → HS, HS2
      ];
      const setAttendance = (slot: number, marker: string) => {
        const item = base.find((b) => b.value === slot);
        if (item && item.attendance === '') item.attendance = marker;
      };
      for (let main = 0; main < 3; main++) {
        const code = attendCodes[main];
        const [firstNour, secondNour] = nourishmentsForMain[main];
        switch (code) {
          case 1:
            setAttendance(main, '(SM)'); break;
          case 2:
            setAttendance(main, '(NP)');
            setAttendance(firstNour, '(NP)');
            setAttendance(secondNour, '(NP)');
            break;
          case 3:
            setAttendance(main, '(NP)');
            setAttendance(secondNour, '(NP)');
            break;
          case 4:
            setAttendance(main, '(NP)'); break;
          case 5:
            setAttendance(main, '(NP)');
            setAttendance(firstNour, '(NP)');
            break;
          case 6:
            setAttendance(main, '(NP)'); break;
          case 7:
            setAttendance(main, '(NP)');
            setAttendance(firstNour, '(NP)');
            break;
          case 8:
            setAttendance(main, '(NP)'); break;
          case 9:
            setAttendance(main, '(NP)');
            setAttendance(firstNour, '(NP)');
            break;
          default: break;
        }
      }
    }

    return base.map((b) => ({
      value: b.value,
      label: b.attendance ? `${b.label} ${b.attendance}` : b.label,
    }));
  }, [facilityMealNames, isProduction, selectedMealtime, selectedPatient]);


  // Auto-pick the first patient if none is selected once the list lands.
  useEffect(() => {
    if (!open) return;
    if (selectedPatientId != null) return;
    if (patients.length === 0) return;
    setSelectedPatientId(patients[0].fKey_Patient);
  }, [open, selectedPatientId, patients]);

  // Print — duplicates the WinForms `frmMain.TrayTicketVisualizerPrint_Handler`
  // exactly. Builds a `TrayTicketVisualizerSettings` payload
  // (`TrayTicketVisualizerSettings.vb`) and queues it via the shared
  // `useQueueReport` hook. On success `watchAndOpen(..., { print: true })`
  // raises the persistent banner and opens the print dialog on the
  // rendered PDF as soon as the report finishes generating. WinForms'
  // `PrintReport(..., autoShow: True, HiddenInDashboard)` shows a preview
  // first; here the visualizer itself is that preview.
  const queueReport = useQueueReport(facilityId);
  const { watchAndOpen } = useReportAutoOpener();

  /**
   * WinForms `paperType` values (Production facility) —
   * `frmDietech_Prd_TrayTicketVisualizer.vb:661-673`. Facility
   * setting `TrayTicketPaperType` (0-5) is mapped to a visual
   * paper-type index (0-4) that gets sent to the server on print.
   *   0, 1 → 3 (4 Part)
   *   2    → 2 (3 Part)
   *   3    → 0 (1 Part - Continuous)
   *   4    → 1 (1 4x4)
   *   5    → 4 (3 Part - Landscape)
   */
  const facilityDefaultPaperType = (() => {
    switch (facilitySettings?.trayTicketPaperType ?? 0) {
      case 0: case 1: return 3;
      case 2: return 2;
      case 3: return 0;
      case 4: return 1;
      case 5: return 4;
      default: return 3;
    }
  })();

  // Session-scoped paper type — starts at the facility default,
  // user can change via the Print split-button dropdown. Same
  // pattern WinForms uses (paperType is a local field on the
  // form, not persisted).
  const [paperType, setPaperType] = useState<number>(facilityDefaultPaperType);
  useEffect(() => {
    if (open) setPaperType(facilityDefaultPaperType);
  }, [open, facilityDefaultPaperType]);

  const [printMenuAnchor, setPrintMenuAnchor] = useState<HTMLElement | null>(null);

  // Mobile toolbar drawer — replaces the inline ribbon on narrow
  // viewports. Reset closed each time the dialog opens.
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  useEffect(() => { if (open) setMobileDrawerOpen(false); }, [open]);

  // Notices used to own their own open state behind a vertical tab. Now
  // that the trigger is a counted button in the title bar, the dialog owns
  // it — the button has to know the count anyway to render the badge.
  const [noticesOpen, setNoticesOpen] = useState(false);
  const notices = detailQuery.data?.notices ?? [];
  const realNoticeCount = countRealNotices(notices);
  // Auto-open when a fresh fetch turns up something to flag. The vertical
  // tab did this too, and it is the one behaviour worth carrying over: a
  // notice is a clinical flag on a tray, and a badge someone has to notice
  // is weaker than a panel that opens itself. Only on the rising edge, so
  // closing it stays closed while the count holds steady, and only where
  // there is room for it — see the gate below.
  const prevRealCountRef = useRef(0);
  useEffect(() => {
    if (realNoticeCount > 0 && prevRealCountRef.current === 0 && !isNarrow) setNoticesOpen(true);
    prevRealCountRef.current = realNoticeCount;
  }, [realNoticeCount, isNarrow]);
  useEffect(() => { if (open) setNoticesOpen(false); }, [open]);

  /** Paper-type options — labels match the WinForms bar-item
   *  captions; `value` is the visual paperType sent to the server. */
  const PAPER_TYPE_OPTIONS: readonly { value: number; label: string }[] = [
    { value: 0, label: '1 Part - Continuous' },
    { value: 1, label: '1 Part - 4"x4"' },
    { value: 2, label: '3 Part' },
    { value: 3, label: '4 Part' },
    { value: 4, label: '3 Part - Landscape' },
  ];

  // Build + queue a `TrayTicketVisualizerSettings` payload —
  // duplicates `frmMain.TrayTicketVisualizerPrint_Handler` in
  // WinForms exactly. Uses `useQueueReport` (generic queue endpoint
  // that expects `settingsJson`) with the auto-opener's
  // `watchAndOpen(..., { print: true })` on success so the completed
  // PDF goes to the print dialog behind the persistent banner.
  // Category mirrors WinForms' `PrintReport(..., autoShow: True,
  // ReportCategory.HiddenInDashboard)`.
  const handlePrintWith = async (
    paperTypeToUse: number,
    printDateToUse: string,
    numDaysToUse: number,
    mealtimesToUse: number[],
  ) => {
    if (!selectedPatientId) {
      addNotification({ message: `Select a ${residentLabelLower} before printing.`, type: 'info' });
      return;
    }
    if (mealtimesToUse.length === 0) {
      addNotification({ message: 'Select at least one mealtime.', type: 'info' });
      return;
    }
    // WinForms `TrayTicketVisualizerSettings.LstMealtimes` is a
    // List(Of Integer()) — one Integer() per day. All days use the
    // same mealtime selection from the popup, so build N copies.
    const lstMealtimes: number[][] = [];
    for (let i = 0; i < Math.max(1, numDaysToUse); i++) {
      lstMealtimes.push([...mealtimesToUse]);
    }
    // Single-mealtime shortcut mirrors the `Else` branch in
    // `frmMain.TrayTicketVisualizerPrint_Handler`.
    const singleMealtime = mealtimesToUse.length === 1 ? mealtimesToUse[0] : 0;

    const useProduction = !!(facilitySettings as Record<string, unknown> | null)?.useProduction;
    const patientFullName = selectedPatient?.fullName ?? '';
    // Menu header for the print date (WinForms visualizer .vb:501 passes
    // `menuCycleInUseInfo.PKey_Menu_Header`). Wait for the print date's own
    // lookup: the popup keeps showing the previous date's cycle while the
    // next one loads, and falling back to that (or to 0, which prints
    // nothing) sent the wrong menu when Print was clicked right after a
    // date change.
    let fKeyMenuHeader = 0;
    try {
      const info = await queryClient.fetchQuery({
        queryKey: trayTicketKeys.menuCycle(facilityId, printDateToUse),
        queryFn: () => fetchTrayTicketMenuCycle(facilityId, printDateToUse),
        staleTime: 5 * 60 * 1000,
      });
      fKeyMenuHeader = Number(info?.pKey_Menu_Header) > 0 ? Number(info.pKey_Menu_Header) : 0;
    } catch {
      addNotification({ message: 'The menu cycle could not be loaded, so the tray tickets were not printed. Try again.', type: 'error' });
      return;
    }
    if (fKeyMenuHeader <= 0) {
      addNotification({ message: 'There is no menu cycle for this date, so the tray tickets were not printed.', type: 'warning' });
      return;
    }

    const settings = {
      // Base ReportSettingsBase fields
      FKey_Facility: facilityId,
      ProgramName: 'Dietech',
      // TrayTicketVisualizerSettings fields (mirror
      // `TrayTicketVisualizerSettings.vb` property-for-property)
      DateFor: printDateToUse,
      PaperType: paperTypeToUse,
      FKey_Patient: selectedPatientId,
      StrFullName: patientFullName,
      Mealtime: singleMealtime,
      LstMealtimes: lstMealtimes,
      FKey_Menu_Header: fKeyMenuHeader,
      FlagToGo: false,
      FlagText: '',
      UseProduction: useProduction,
      ServeAtDateTimeEnabled: false,
      ServeAtDateTime: new Date(0).toISOString(),
    };

    // Report name mirrors `TrayTicketVisualizerSettings.ReportNameOverride`.
    const reportName = mealtimesToUse.length === 1
      ? `Tray Ticket Visualizer - ${patientFullName} - ${mealtimeName}`
      : `Tray Ticket Visualizer - ${patientFullName} - Multiple Times`;

    try {
      const result = await queueReport.mutateAsync({
        reportType: 'TrayTicketVisualizer',
        fKey_User: userId,
        fKey_RS_Report: 84, // RSReportEnums.TrayTicketVisualizer
        settingsJson: JSON.stringify(settings),
        reportName,
        dateFor: printDateToUse,
        // ReportCategory.HiddenInDashboard = 1 — mirrors WinForms
        // `PrintReport(..., ReportCategory.HiddenInDashboard)` at
        // frmMain.vb:3135. Keeps on-demand auto-show tray tickets
        // out of the reports dashboard grid; they're accessed via
        // the auto-open banner instead.
        category: 1,
      });
      if (result?.success && result.reportDocumentId != null) {
        // Persistent spinner banner until the ticket is rendered, then
        // straight to the browser's print dialog on its PDF. The
        // visualizer already is the preview, so opening the report
        // viewer first only made the user wait for it and find Print a
        // second time. Phones/tablets, or a PDF that won't print in the
        // page, still get the viewer. No extra toast: `watchAndOpen`
        // raises its own banner.
        watchAndOpen(result.reportDocumentId, reportName, { print: true });
      } else if (result?.success) {
        addNotification(reportQueuedToast(`${reportName} queued`));
      } else {
        addNotification({
          message: result?.message || `Failed to queue ${reportName}`,
          type: 'error',
        });
      }
    } catch {
      addNotification({ message: `Error queuing ${reportName}`, type: 'error' });
    }
  };

  // Print options dialog state — pops before queueing so the user
  // can pick date range, mealtimes, etc. (mirrors the WinForms
  // "Print Report..." popup off the visualizer).
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printDate, setPrintDate] = useState<string>(selectedDate);
  const [printNumDays, setPrintNumDays] = useState<number>(1);
  const [printMealtimes, setPrintMealtimes] = useState<number[]>([selectedMealtime]);
  const [printPaperType, setPrintPaperType] = useState<number>(paperType);

  // Menu-cycle lookup for the PRINT dialog's date — separate from
  // the main visualizer's `menuCycleQuery` so its Menu Cycle line
  // refreshes as the user picks a different day in the popup.
  // Enabled only while the popup is open so the request doesn't
  // fire against a stale/closed dialog date.
  const printMenuCycleQuery = useTrayTicketMenuCycle(facilityId, printDate, printDialogOpen);
  // Hold the last resolved menu-cycle payload so the display keeps
  // the old value visible while the next date's request is in
  // flight — swapping to `undefined` during `isFetching` was
  // causing a visible flash when a user hit the date chevrons.
  const printMenuCycleLastData = useRef<typeof printMenuCycleQuery.data>(undefined);
  if (printMenuCycleQuery.data !== undefined) {
    printMenuCycleLastData.current = printMenuCycleQuery.data;
  }
  const printMenuCycleDisplay = printMenuCycleQuery.data ?? printMenuCycleLastData.current;

  // Reseed print dialog defaults from the current toolbar state
  // each time it's opened. Only main mealtimes (0/1/2) participate
  // in the print form — WinForms limits the mealtime checkboxes to
  // Breakfast / Lunch / Supper.
  const openPrintDialog = (paperTypeSeed: number) => {
    // Drop any cycle payload cached from a prior open so the fresh
    // fetch's spinner shows against an empty block instead of the
    // last session's cycle name flashing in briefly.
    printMenuCycleLastData.current = undefined;
    setPrintDate(selectedDate);
    setPrintNumDays(1);
    setPrintMealtimes(selectedMealtime >= 0 && selectedMealtime < 3 ? [selectedMealtime] : [0]);
    setPrintPaperType(paperTypeSeed);
    setPrintDialogOpen(true);
  };

  const handleTogglePrintMealtime = (slot: number) => {
    setPrintMealtimes((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot].sort()
    );
  };
  const handleToggleAllPrintMealtimes = () => {
    setPrintMealtimes((prev) => (prev.length === 3 ? [] : [0, 1, 2]));
  };

  // Fires the actual queue call with values from the dialog form.
  const handlePrintSubmit = () => {
    setPrintDialogOpen(false);
    void handlePrintWith(printPaperType, printDate, printNumDays, printMealtimes);
  };

  // Main print click — opens the options dialog (was firing the
  // queue directly).
  const handlePrint = () => { openPrintDialog(paperType); };

  /**
   * The visualizer's own commands, published while it is open.
   *
   * It is a dialog, so its verbs sit in a toolbar she has to find by tabbing
   * across it. Registered here they are innermost, which puts them at the top
   * of Ctrl+/ ahead of the record's thirty section commands — the right order
   * when the ticket is the thing in front of her.
   *
   * Handlers behind a ref so this depends on nothing that changes per render:
   * re-registering notifies the registry's subscribers, which renders again.
   */
  const ttvActionsRef = useRef({ handlePrint, onClose });
  ttvActionsRef.current = { handlePrint, onClose };
  useEffect(() => {
    // Only while actually open. The dialog stays mounted with open={false},
    // so registering unconditionally offered "Print this tray ticket" when
    // there was no ticket — a command that cannot act — and pushed the
    // record's own verbs out of the palette's capped list.
    if (!open) return undefined;
    return registerShortcuts([
    {
      id: 'tray-ticket-print',
      key: 'p',
      ctrl: true,
      label: 'Print this tray ticket',
      category: 'general',
      whenModalOpen: 'fire',
      handler: () => ttvActionsRef.current.handlePrint(),
    },
    {
      id: 'tray-ticket-close',
      key: '',
      label: 'Close the Tray Ticket Visualizer',
      category: 'general',
      whenModalOpen: 'fire',
      handler: () => ttvActionsRef.current.onClose(),
    },
    ]);
  }, [open]);

  // Refresh must reach everything the dialog is showing. It used to
  // call `listQuery.refetch()`, which is the no-op stub left behind
  // when the heavy list call was retired in favour of the lightweight
  // patient-list endpoint — so the resident dropdown and menu-cycle
  // header were never actually refreshed. That went unnoticed while
  // closing the dialog wiped every cache, because reopening was what
  // really refetched the list. Those caches now survive a close (see
  // the close effect above), which makes this the only way to force a
  // mid-service admission or discharge to appear.
  const handleRefresh = () => {
    void menuCycleQuery.refetch();
    void patientListQuery.refetch();
    if (selectedPatientId) void detailQuery.refetch();
  };

  const mealtimeName = mealtimeOptions.find((m) => m.value === selectedMealtime)?.label ?? '';

  // Banner needs the RAW mealtime name (no `(SM)` / `(NP)` marker).
  // The radio-label variant appends the attendance marker; the tray
  // banner mirrors WinForms `strMeals(mealMain)` which is the base
  // name only.
  const mealtimeBaseName = (facilityMealNames[selectedMealtime] ?? '').trim()
    || (MEALTIME_DEFAULTS[selectedMealtime] ?? '');

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        // Named, and pointed at its own title. Unnamed, this announced as just
        // "dialog" on open — a screen reader user had no way to know which of
        // the app's many dialogs had appeared. The title exists on screen; it
        // simply was not wired to the dialog.
        aria-labelledby="tray-ticket-visualizer-title"
        maxWidth={false}
        fullWidth
        fullScreen={isFullScreen}
        PaperProps={{
          sx: isFullScreen
            // Edge-to-edge on a phone. A 95vw dialog with rounded corners
            // wastes ~40px of a 390px screen on a decorative gutter, and the
            // ticket is the one thing that needs the width.
            ? { m: 0, borderRadius: 0, maxWidth: '100%', width: '100%', height: '100%' }
            : { width: '95vw', maxWidth: 1200, height: '92vh', m: 1 },
        }}
      >
        {/* Title bar. Was a saturated `primary.main` slab captioned
            "mirrors WinForms modal title" — the single loudest tell that
            this screen came from a desktop app. A dialog title does not
            need to shout its own chrome colour; a light surface with a
            hairline under it reads as a document header, which is what
            this is. */}
        <Box
          sx={{
            bgcolor: 'background.paper',
            color: 'text.primary',
            padding: '8px 8px 8px 16px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: 1,
            borderColor: 'divider',
            flexShrink: 0,
          }}
        >
          {/* Hamburger — narrow viewports only. Opens the toolbar
              drawer. Sits to the LEFT of the title so it's exactly
              where mobile users expect an app-menu affordance. */}
          {isDrawer && (
            <IconButton
              onClick={() => setMobileDrawerOpen(true)}
              aria-label="Open toolbar"
              // 44px is the coarse-pointer target size the rest of the app
              // enforces in responsive.css; this button is only ever shown
              // on the sizes where that applies.
              sx={{ color: 'inherit', mr: 0.5, width: 44, height: 44 }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          )}
          <i className="bi bi-easel" style={{ fontSize: 15, color: 'var(--text-secondary, #6b7280)' }} />
          <Typography
            id="tray-ticket-visualizer-title"
            // role/aria-level rather than a heading tag: the sx below carries
            // the visual treatment, and swapping the element would restyle the
            // bar. This gives the dialog a name and gives the screen a heading
            // to navigate to — it had none.
            role="heading"
            aria-level={2}
            sx={{ flex: 1, fontSize: 14, fontWeight: 600 }}
          >
            Tray Ticket Visualizer
          </Typography>
          {/* Notices moved here from the vertical tab welded to the right
              edge of the body. A rotated-text tab is a docking-window
              idiom with no web equivalent, and it cost the ticket 28px of
              width at every size. As a counted button the badge also says
              whether opening it is worth the trip. */}
          <Tooltip title="Notices">
            <span>
              <IconButton
                onClick={() => setNoticesOpen(true)}
                aria-label={realNoticeCount > 0 ? `Notices (${realNoticeCount})` : 'Notices'}
                disabled={!selectedPatientId}
                sx={{ color: 'inherit', width: 44, height: 44 }}
              >
                <Badge badgeContent={realNoticeCount} color="error" max={99}>
                  <i className="bi bi-exclamation-circle" style={{ fontSize: 16 }} />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={onClose} aria-label="Close" sx={{ color: 'inherit', width: 44, height: 44 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Ribbon toolbar — grouped sections match WinForms layout.
            On WIDE viewports (>=lg): rendered inline under the
            title bar. On NARROW (<lg): the inline toolbar hides
            entirely and the hamburger opens a `<Drawer>` that
            mounts the same toolbar content in a stacked vertical
            layout. See `toolbarContent` variable further down. */}
        <Box
          sx={{
            display: isDrawer ? 'none' : 'flex',
            alignItems: 'stretch',
            gap: 1,
            rowGap: 1,
            padding: '6px 8px',
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
            // Lock all groups (including the mealtime radios) into
            // ONE row on the desktop toolbar. Wrap-on-overflow was
            // causing the mealtime group to bounce to a second row
            // whenever a subtle inner size change (disable state on
            // an inner control) pushed the total width past the
            // dialog Paper's inner width. On narrow the whole
            // toolbar hides and the drawer stacks vertically, so
            // nowrap here doesn't affect the mobile layout.
            //
            // Between 900 and 1200 it DOES wrap: that is the middle stage
            // this dialog previously skipped. Wrapping to two rows there
            // is strictly better than the alternative it used to take,
            // which was hiding the toolbar outright.
            flexWrap: isNarrow ? 'wrap' : 'nowrap',
          }}
        >
          {/* Group: Visualizer actions — natural width; row-break
              below on narrow puts Patient + Menu on their own row. */}
          <RibbonGroup sx={{ justifyContent: 'center' }}>
            {/* Print split button — main click prints with the
                currently-selected paper type; chevron opens the
                menu to change it (WinForms `mbtnRibbonPrint` +
                sub-items `mbtnPrint_1Part/3Print/4Part/4x4/3Landscape`).
                Selecting a paper type also prints immediately per
                the WinForms handler at
                frmDietech_Prd_TrayTicketVisualizer.vb:511
                (`XtraBar_PerformClick(mbtnRibbonPrint)`). */}
            <Box sx={{ display: 'inline-flex' }}>
              <Button
                size="small"
                variant="outlined"
                onClick={handlePrint}
                disabled={!selectedPatientId || queueReport.isPending || isInitialLoading || isDetailLoading}
                sx={{
                  textTransform: 'none',
                  minWidth: 0,
                  px: 1.25,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  borderRight: 'none',
                }}
              >
                <i className="bi bi-printer" style={{ marginRight: 4 }} /> Print
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => setPrintMenuAnchor(e.currentTarget)}
                disabled={!selectedPatientId || queueReport.isPending || isInitialLoading || isDetailLoading}
                aria-label="Choose paper type"
                sx={{
                  textTransform: 'none',
                  minWidth: 0,
                  px: 0.5,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  // No left border — Print already renders the shared
                  // edge (with `borderRight: none` there stacks nothing).
                  // Without this, both buttons' outlined borders touch
                  // and read as a black bar.
                  borderLeft: 'none',
                }}
              >
                <i className="bi bi-caret-down-fill" style={{ fontSize: 10 }} />
              </Button>
            </Box>
            <Menu
              anchorEl={printMenuAnchor}
              open={Boolean(printMenuAnchor)}
              onClose={() => setPrintMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              // Lifts above the mobile Drawer (which itself sits above
              // the parent Dialog). Otherwise the menu opens behind
              // the drawer when triggered from a drawer button.
              sx={{ zIndex: (theme) => theme.zIndex.modal + 20 }}
            >
              {PAPER_TYPE_OPTIONS.map((opt) => (
                <MenuItem
                  key={opt.value}
                  selected={opt.value === paperType}
                  onClick={() => {
                    setPaperType(opt.value);
                    setPrintMenuAnchor(null);
                    // Open the print options dialog seeded with
                    // the freshly-picked paper type. WinForms shows
                    // the same "Print Report..." popup for either
                    // main-Print or paper-type click.
                    openPrintDialog(opt.value);
                  }}
                  dense
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {opt.value === paperType ? <CheckIcon fontSize="small" /> : null}
                  </ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontSize: 14 }}>
                    {opt.label}
                  </ListItemText>
                </MenuItem>
              ))}
            </Menu>
            <Button
              size="small"
              variant="outlined"
              onClick={handleRefresh}
              disabled={isInitialLoading || isDetailLoading}
              sx={{ textTransform: 'none', minWidth: 0, px: 1.25 }}
            >
              <i className="bi bi-arrow-clockwise" style={{ marginRight: 4 }} /> Refresh
            </Button>
            {/* Vertical divider between the Print/Refresh sub-group
                and Explain. Explicit width/style/color triplet so
                it renders in the same divider swatch as the ribbon-
                group vertical borders (MUI sx `border: 1` shorthand
                falls back to text color). Hidden on narrow (mobile)
                where all group dividers are dropped for cleanliness. */}
            <Box
              sx={{
                alignSelf: 'stretch',
                borderLeftWidth: '1px',
                borderLeftStyle: 'solid',
                borderLeftColor: 'divider',
                mx: 0.5,
              }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => setExplainerOpen(true)}
              disabled={!selectedPatientId || isInitialLoading || isDetailLoading}
              sx={{ textTransform: 'none', minWidth: 0, px: 1.25 }}
            >
              <i className="bi bi-info-circle" style={{ marginRight: 4 }} /> Explain
            </Button>
          </RibbonGroup>

          {/* Group: Resident — Select on top, demographics stacked
              below (one line each) matching the WinForms
              `barMe_ResidentInfo` panel: Gender / DOB / Unit-Rm-B.
              Same isNarrow-driven responsive rules as RibbonGroup. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              borderRightWidth: '1px',
              borderRightStyle: 'solid',
              borderRightColor: 'divider',
              px: 1,
              pb: 0.5,
              pt: 0,
              width: 'auto',
              minWidth: 265,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 44 }}>
              {/* The shared dropdown (SelectField): typing narrows on name and diet
                  order, the way the old Find box did; guests stay indented under
                  their host. Was a raw MUI Select painted common.white, a solid
                  white box with light text in dark mode (UI audit, 2026-10-24). */}
              <ResidentPicker
                patients={sortedPatients}
                value={selectedPatientId}
                onChange={setSelectedPatientId}
                loading={patientListQuery.isLoading}
                disabled={isInitialLoading || isDetailLoading}
                residentLabel={residentLabelLower}
                residentPlural={residentPluralLower}
                width={245}
              />
            </Box>
            {selectedPatient && (
              <Box sx={{ mt: 0.5 }}>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
                  Gender: {selectedPatient.gender ?? 'U'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
                  DOB: {selectedPatient.birthDate
                    ? new Date(selectedPatient.birthDate).toLocaleDateString('en-US')
                    : 'None'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
                  {/* Mirrors WinForms `Fill_MenuResidentInfo`
                      (.vb:3502-3512): a `/` prefix is added ONLY
                      when the field is populated, so an empty Room
                      or Bed doesn't leave a dangling slash. */}
                  Unit/Rm/B: {selectedPatient.unit || 'None'}
                  {selectedPatient.roomNo ? `/${selectedPatient.roomNo}` : ''}
                  {selectedPatient.bedNo ? `/${selectedPatient.bedNo}` : ''}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Group: Menu Cycle — WinForms lays this out as a vertical
              stack: date editor on top, then a "Menu Cycle" section
              header, then the cycle name, then day-of-week + MC-day
              line. No separate group caption; the header serves as
              the label. Same isNarrow-driven responsive rules as
              RibbonGroup. */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              borderRightWidth: '1px',
              borderRightStyle: 'solid',
              borderRightColor: 'divider',
              px: 1,
              pb: 0.5,
              pt: 0,
              width: 'auto',
              minWidth: 220,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 44 }}>
              {/* Named "Ticket date" so a screen reader says what the
                  date is for, not just the date. */}
              <DateField
                ariaLabel="Ticket date"
                value={selectedDate}
                onValueChanged={(v) => { if (v) setSelectedDate(v); }}
                onOpened={() => setDateEditOpen(true)}
                onClosed={() => setDateEditOpen(false)}
                displayFormat="M/D/YYYY"
                width={233}
                height={34}
                disabled={isInitialLoading || isDetailLoading}
                showStepButtons
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary' }}>
                Menu Cycle
              </Typography>
              {menuCycleQuery.isLoading && <CircularProgress size={10} thickness={5} sx={{ ml: '20px' }} />}
            </Box>
            {menuCycleQuery.data?.menuCycleName ? (
              <>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
                  {menuCycleQuery.data.menuCycleName}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
                  {new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' })}
                  {menuCycleQuery.data.menuDay > 0 && <> {' | '} MC Day {menuCycleQuery.data.menuDay}</>}
                </Typography>
              </>
            ) : (
              !menuCycleQuery.isLoading && menuCycleQuery.data && (
                <Typography sx={{ fontSize: 11, color: 'text.disabled', fontStyle: 'italic', lineHeight: 1.3 }}>
                  No active menu cycle
                </Typography>
              )
            )}
          </Box>

          {/* Group: Mealtime — segmented chips, one row that wraps.
              Was a 3-column column-major radio grid pinned to a hard
              `width: 340`, copied from the WinForms `beRadMealtime`
              panel. Reading order in a column-major grid is a desktop
              convention nobody expects on the web (you scan down, not
              across), and the fixed width was load-bearing: the ribbon
              had to be `nowrap` because that grid could not shrink.
              Chips wrap on their own, which is what let the middle
              breakpoint exist at all.
              Nourishment slots (3-8) appear only on Production
              facilities; attendance markers "(SM)"/"(NP)" are
              appended per WinForms `SetPatientAttendance`. */}
          <RibbonGroup
            sx={{
              justifyContent: 'center',
              // Last group in the row — no trailing divider at any
              // breakpoint. Overrides the `borderRightWidth` longhand
              // set on the RibbonGroup default (shorthand `borderRight`
              // can't reliably override a longhand set elsewhere).
              borderRightWidth: 0,
              pr: 0,
              mr: 0,
            }}
          >
            <ToggleButtonGroup
              value={selectedMealtime}
              exclusive
              disabled={isInitialLoading || isDetailLoading}
              onChange={(_e, v) => { if (v !== null) setSelectedMealtime(Number(v)); }}
              aria-label="Mealtime"
              // The whole group is disabled while a ticket loads. Say so,
              // rather than leaving dimmed chips unexplained.
              aria-busy={isInitialLoading || isDetailLoading || undefined}
              title={isInitialLoading || isDetailLoading ? 'Loading the ticket. Mealtimes can be changed when it finishes.' : undefined}
              className="ttv-mealtime-chips"
              sx={TTV_MEALTIME_CHIPS}
            >
              {mealtimeOptions.map((m) => (
                <ToggleButton key={m.value} value={m.value} aria-label={m.label}>
                  {m.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </RibbonGroup>
        </Box>

        {/* Mobile toolbar drawer — right-anchored side panel that
            opens from the hamburger in the title bar. Placeholder
            content for now; the plan is to extract the ribbon
            groups above into a shared JSX variable and reference
            it here so we don't duplicate ~400 lines of source.
            That extraction is a follow-up task. */}
        <Drawer
          anchor="left"
          open={mobileDrawerOpen && isNarrow}
          onClose={() => setMobileDrawerOpen(false)}
          // z-index bumped above the parent Dialog (default modal
          // z-index) — otherwise the Drawer's overlay + panel mount
          // BEHIND the Dialog Paper and are invisible.
          sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: 340,
              maxWidth: '90vw',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 600 }}>Visualizer</Typography>
            <IconButton size="small" onClick={() => setMobileDrawerOpen(false)} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {/* Print / Refresh / Explain moved to the mobile summary
              banner (above the diet order block) so the drawer
              keeps only the selectors (resident / date / mealtime). */}

          {/* --- Resident --- */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {residentLabel}
            </Typography>
            <ResidentPicker
              patients={sortedPatients}
              value={selectedPatientId}
              onChange={setSelectedPatientId}
              loading={patientListQuery.isLoading}
              disabled={isInitialLoading || isDetailLoading}
              residentLabel={residentLabelLower}
              residentPlural={residentPluralLower}
              width="100%"
            />
            {selectedPatient && (
              <Box>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.3 }}>
                  Gender: {selectedPatient.gender ?? 'U'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.3 }}>
                  DOB: {selectedPatient.birthDate
                    ? new Date(selectedPatient.birthDate).toLocaleDateString('en-US')
                    : 'None'}
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.3 }}>
                  Unit/Rm/B: {selectedPatient.unit || 'None'}
                  {selectedPatient.roomNo ? `/${selectedPatient.roomNo}` : ''}
                  {selectedPatient.bedNo ? `/${selectedPatient.bedNo}` : ''}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* --- Menu Cycle --- */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DateField
                ariaLabel="Ticket date"
                value={selectedDate}
                onValueChanged={(v) => { if (v) setSelectedDate(v); }}
                onOpened={() => setDateEditOpen(true)}
                onClosed={() => setDateEditOpen(false)}
                displayFormat="M/D/YYYY"
                width={233}
                height={34}
                disabled={isInitialLoading || isDetailLoading}
                showStepButtons
              />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
                Menu Cycle
              </Typography>
              {menuCycleQuery.isLoading && <CircularProgress size={12} thickness={5} sx={{ ml: '20px' }} />}
            </Box>
            {menuCycleQuery.data?.menuCycleName ? (
              <>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.3 }}>
                  {menuCycleQuery.data.menuCycleName}
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.3 }}>
                  {new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' })}
                  {menuCycleQuery.data.menuDay > 0 && <> {' | '} MC Day {menuCycleQuery.data.menuDay}</>}
                </Typography>
              </>
            ) : (
              !menuCycleQuery.isLoading && menuCycleQuery.data && (
                <Typography sx={{ fontSize: 13, color: 'text.disabled', fontStyle: 'italic', lineHeight: 1.3 }}>
                  No active menu cycle
                </Typography>
              )
            )}
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* --- Mealtime — single vertical column (all radios
              stack top-to-bottom, one per row). */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Mealtime
            </Typography>
            <FormControl disabled={isInitialLoading || isDetailLoading}>
              <RadioGroup
                value={selectedMealtime}
                onChange={(_e, v) => setSelectedMealtime(Number(v))}
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                {mealtimeOptions.map((m) => (
                  <FormControlLabel
                    key={m.value}
                    value={m.value}
                    control={<Radio size="small" sx={{ py: 0.25, px: 0.5 }} />}
                    label={<span style={{ fontSize: 15, whiteSpace: 'nowrap' }}>{m.label}</span>}
                    sx={{ mr: 0, ml: 0 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        </Drawer>

        {/* Diet Order / Allergies banner — mirrors the WinForms
            `lblDietType1..5` + `lblConsist1..5` (left) and
            `lblAllergies` (right) row that sits between the ribbon
            and the tray body. Font sizing matches the tray-body
            classes `.ttvc-diet-line` (16px / 700), `.ttvc-consist-line`
            (15px / 700), `.ttvc-allergies` (14px / 600 red) so the
            visual weight is consistent whether the info reads in
            the banner or the tray. Duplicate rows inside the tray
            body are suppressed via the sibling
            `TrayTicketVisualizerContent` (see props there). */}
        {/* Mobile-only summary strip — resident name + selected
            date + current mealtime label on the LEFT, and
            Print / Refresh / Explain icon-only buttons on the
            RIGHT. Print is still a split button (chevron opens
            the paper-type menu). Shown only once the ribbon has
            actually folded into the drawer — gating it on `isNarrow`
            put these buttons on screen alongside the ribbon copies of
            themselves for the whole 900-1200 range. */}
        {isDrawer && selectedPatient && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }} noWrap>
                {selectedPatient.fullName}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                {new Date(`${selectedDate}T00:00:00`).toLocaleDateString('en-US', {
                  weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
                })}
                {mealtimeName ? ` · ${mealtimeName}` : ''}
              </Typography>
            </Box>
            {/* Action cluster — proper outlined toolbar buttons
                (icon-only). Print + chevron sit as a joined split
                button; Refresh + Explain are stand-alone. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ display: 'inline-flex' }}>
                <Tooltip title="Print">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handlePrint}
                      disabled={!selectedPatientId || queueReport.isPending || isInitialLoading || isDetailLoading}
                      aria-label="Print"
                      sx={{
                        minWidth: 0,
                        px: 1,
                        py: 0.5,
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        borderRight: 'none',
                      }}
                    >
                      <i className="bi bi-printer" style={{ fontSize: 16 }} />
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Choose paper type">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => setPrintMenuAnchor(e.currentTarget)}
                      disabled={!selectedPatientId || queueReport.isPending || isInitialLoading || isDetailLoading}
                      aria-label="Choose paper type"
                      sx={{
                        minWidth: 0,
                        px: 0.5,
                        py: 0.5,
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                      }}
                    >
                      <i className="bi bi-caret-down-fill" style={{ fontSize: 10 }} />
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              <Tooltip title="Refresh">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleRefresh}
                    disabled={isInitialLoading || isDetailLoading}
                    aria-label="Refresh"
                    sx={{ minWidth: 0, px: 1, py: 0.5 }}
                  >
                    <i className="bi bi-arrow-clockwise" style={{ fontSize: 16 }} />
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Explain">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setExplainerOpen(true)}
                    disabled={!selectedPatientId || isInitialLoading || isDetailLoading}
                    aria-label="Explain"
                    sx={{ minWidth: 0, px: 1, py: 0.5 }}
                  >
                    <i className="bi bi-info-circle" style={{ fontSize: 16 }} />
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        )}

        {/* Neutral canvas the sheet floats on. Edge-to-edge on a phone —
            a shadowed page inset inside a full-screen dialog is 32px of
            decoration on a screen that has none to spare. */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            overflow: 'hidden',
            p: isFullScreen ? 0 : 2,
            bgcolor: isFullScreen ? 'background.paper' : 'var(--ttv-canvas, #eef0f3)',
          }}
        >
          <Box
            sx={{
              ...TTV_SHEET,
              boxShadow: isFullScreen ? 'none' : '0 1px 3px rgba(0,0,0,0.10), 0 8px 24px rgba(0,0,0,0.08)',
              borderRadius: isFullScreen ? 0 : '6px',
            }}
          >
        {selectedPatient && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
              px: 2,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              // Match the tray-body item font so diet order,
              // consistencies, and allergies read as the same
              // typographic voice as the ticket items.
              fontFamily: '"Times New Roman", Times, serif',
            }}
          >
            {/* Left: diet types (16px/700) then consistencies (15px/700
                yellow highlight) — same sizing as `.ttvc-diet-line` /
                `.ttvc-consist-line`. Gated on `selectedPatientDetail`
                because the effective diet for this date/mealtime can
                differ from the current-effective diet on tblPatient —
                adjustments and temp diets are applied by the menu-
                generation call. Only render once that has landed. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1, minWidth: 0 }}>
              {/* Each diet-type / consistency renders as its own
                  colored chip using the generator's per-slot
                  Argb_Back / Argb_Fore. Mirrors WinForms
                  `RenderPatientInfo` lines 2967-3018 which read
                  `DietType_{i}_B_Argb` / `_F_Argb` off the generator's
                  PatientInfo row. These reflect the OVERRIDDEN diet
                  for this date/mealtime (temp diets, adjustments) —
                  not the tblDiet_Flucs default. Colors only applied
                  when the facility has `Use_Color` on. */}
              {hasFreshDetail && selectedPatientDetail!.dietTypes && selectedPatientDetail!.dietTypes.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {/* Chip color and position separate these groups visually;
                      spoken, they run together. See the consistency group
                      below for the other half. */}
                  <span className="sr-only">Diet: </span>
                  {selectedPatientDetail.dietTypes.map((dt, i) => {
                    const colors = selectedPatientDetail.dietTypeColors?.[i];
                    const bg = colorConfig?.useColor && colors?.backArgb
                      ? argbToHex(colors.backArgb) : undefined;
                    const fg = colorConfig?.useColor && colors?.foreArgb
                      ? argbToHex(colors.foreArgb) : 'text.primary';
                    return (
                      <Box
                        key={i}
                        component="span"
                        sx={{
                          display: 'inline-block',
                          fontSize: '16px',
                          fontWeight: 700,
                          letterSpacing: '0.3px',
                          color: fg,
                          backgroundColor: bg,
                          px: bg ? 0.5 : 0,
                        }}
                      >
                        {dt}
                      </Box>
                    );
                  })}
                </Box>
              )}
              {hasFreshDetail && selectedPatientDetail!.consistencies && selectedPatientDetail!.consistencies.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <span className="sr-only">Consistency: </span>
                  {selectedPatientDetail.consistencies.map((c, i) => {
                    const colors = selectedPatientDetail.consistencyColors?.[i];
                    const bg = colorConfig?.useColor && colors?.backArgb
                      ? argbToHex(colors.backArgb) : '#FFF176';
                    const fg = colorConfig?.useColor && colors?.foreArgb
                      ? argbToHex(colors.foreArgb) : 'text.primary';
                    return (
                      <Box
                        key={i}
                        component="span"
                        sx={{
                          display: 'inline-block',
                          alignSelf: 'flex-start',
                          fontSize: '15px',
                          fontWeight: 700,
                          letterSpacing: '0.3px',
                          color: fg,
                          backgroundColor: bg,
                          px: 0.5,
                        }}
                      >
                        {c}
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>

            {/* Right: [SF box] [Allergies] — wrapped in a flex row
                so the boxed SF label sits immediately to the left
                of the allergies block. Gated on `hasFreshDetail` so
                Refresh clicks visibly wipe them alongside the diet
                banner / mealtime banner / items instead of holding
                stale cached values. */}
            {hasFreshDetail && (selectedPatient.hasSFCondiment
              || (selectedPatient.allergies && selectedPatient.allergies.length > 0)) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {/* SF Condiments — small red boxed label. Mirrors
                    WinForms `lblSF` (.vb:1653-1654) which sets
                    `Visibility = ShowSF_Condiment` and
                    `ForeColor = Color_SF_Condiments` (hardcoded Red
                    when useColor is on, Diet_Utils.vb:4645). */}
                {selectedPatient.hasSFCondiment && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 32,
                      px: 0.75,
                      py: 0.25,
                      fontFamily: '"Times New Roman", Times, serif',
                      fontSize: '18px',
                      fontWeight: 800,
                      // Text stays colored (red when useColor is on,
                      // else inherited); box border is always black
                      // per the WinForms visual.
                      color: colorConfig?.useColor ? '#c62828' : 'text.primary',
                      border: '1.5px solid #000',
                      lineHeight: 1,
                    }}
                  >
                    SF
                  </Box>
                )}
                {selectedPatient.allergies && selectedPatient.allergies.length > 0 && (
                  <Box
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      textAlign: 'right',
                      color: colorConfig?.useColor && colorConfig.allergiesFg && colorConfig.allergiesFg !== '#000000'
                        ? colorConfig.allergiesFg
                        : '#b91c1c',
                    }}
                  >
                    <span style={{ fontWeight: 800, textTransform: 'uppercase' }}>Allergy: </span>
                    {selectedPatient.allergies.join(', ')}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Body — top padding zeroed so the tray body butts up
            against the diet-order banner (matches WinForms tight
            layout). White background so tray + messages sit on the
            same continuous plane, per the WinForms visualizer. */}
        <DialogContent sx={{
          pt: 0,
          px: 2,
          pb: 2,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          // Sticky-footer pattern. DialogContent fills the Paper
          // (`flex: 1` under the toolbar, capped at 92vh);
          // internal overflow is hidden so the ONLY scroll happens
          // inside the tray body wrapper below.
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          // Positioning context so the patient photo (rendered
          // below via absolute positioning) anchors to the very
          // bottom-right of the dialog body. Also anchors the
          // Notices flyout on the right edge.
          position: 'relative',
        }}>
          {/* Notices — opened from the counted button in the title bar.
              Fed by `detailQuery.data.notices` (computed server-side by
              TrayTicketDAL.ComputeVisualizerNotices). */}
          <TrayTicketNoticesPanel
            notices={notices}
            open={noticesOpen}
            onClose={() => setNoticesOpen(false)}
          />
          {/* Staged reveal — no page-blocking spinner. The toolbar
              (menu cycle text + patient dropdown + date + mealtime)
              renders immediately and populates as `patientListQuery`
              returns. The body stays intentionally EMPTY until that
              list has data AND a patient is selected, so users perceive the
              toolbar filling in first, then the banner + demographics
              appearing, then row-shimmer in the tray items area,
              then real items when `detailQuery` returns. */}
          {/* Shimmer stays visible for the ENTIRE loading window —
              from dialog open until BOTH the patient list AND the
              menu-generation call have returned. This avoids the
              "detail returns before list → no shimmer flashes" race
              that was happening when opening from the grid with a
              pre-selected patient. Body renders only when everything
              is ready so the banner + demographics + items all
              appear together. */}
          {patientListQuery.isLoading ? (
            <MenuItemsShimmer />
          ) : patients.length === 0 ? (
            <Box sx={{ textAlign: 'center', color: 'text.secondary', mt: 6 }}>
              <Typography sx={{ fontSize: 14 }}>No {residentPluralLower} scheduled for this mealtime.</Typography>
            </Box>
          ) : !selectedPatient || !selectedPatientDetail || detailQuery.isFetching ? (
            <MenuItemsShimmer />
          ) : (
            <>
              {/* Feeding status + special devices — WinForms
                  `lblSpecialDevices` (+ feeding-status prefix)
                  band that sits between the diet banner and the
                  mealtime banner. Rendered ONLY when at least one
                  field has content so the band collapses cleanly
                  when there's nothing to show. Colored per
                  `colorConfig.specialDevices` when the facility
                  has `useColor` on. */}
              {selectedPatient.specialDevices && (
                <Box
                  sx={{
                    px: 0,
                    py: '4px',
                    fontSize: '14px',
                    fontFamily: '"Times New Roman", Times, serif',
                  }}
                >
                  {selectedPatient.specialDevices && (
                    <span
                      style={{
                        fontWeight: 600,
                        color: colorConfig?.useColor && colorConfig.specialDevices && colorConfig.specialDevices !== '#000000'
                          ? colorConfig.specialDevices
                          : undefined,
                      }}
                    >
                      {selectedPatient.specialDevices}
                    </span>
                  )}
                </Box>
              )}

              {/* Full-width mealtime banner — mirrors WinForms
                  `lblHeader` construction in `Fill_MenuResidentInfo`.
                  Gated on `selectedPatientDetail` because set / tray /
                  attendance all come from the menu-generation call
                  and vary by date + mealtime. */}
              {hasFreshDetail && (() => {
                const isGuest = selectedPatient.host_FKey_Patient != null;
                const setName = facilitySettings?.setName?.trim() || 'Set';
                const isMain = selectedMealtime >= 0 && selectedMealtime < 3;
                // `?? 0` guards against an older API build that
                // doesn't yet include Set_B/L/D in the DTO — reads
                // as 0 → renders "N/A" rather than crashing.
                const setNo = (selectedMealtime === 1
                  ? selectedPatient.set_L
                  : selectedMealtime === 2
                    ? selectedPatient.set_D
                    : selectedPatient.set_B) ?? 0;
                const trayNo = (selectedMealtime === 1
                  ? selectedPatient.trayLine_L
                  : selectedMealtime === 2
                    ? selectedPatient.trayLine_D
                    : selectedPatient.trayLine_B) ?? 0;

                let header = mealtimeBaseName;
                if (isMain) {
                  if (isGuest) {
                    header = `${mealtimeBaseName} ${setName}: TBD Tray: TBD`;
                  } else {
                    const setStr = setNo > 0 ? String(setNo) : 'N/A';
                    const trayStr = trayNo > 0 ? String(trayNo) : 'N/A';
                    header = `${mealtimeBaseName} ${setName}: ${setStr} Tray: ${trayStr}`;
                  }
                } else if (isGuest) {
                  header = `${mealtimeBaseName} ${setName}: TBD Tray: TBD`;
                }

                // Banner + SME label colors — both come from the same
                // WinForms facility settings (.vb:603-607):
                //   lblHeader.BackColor / lblSME.BackColor → Color_TT_Label_B_Argb
                //   lblHeader.ForeColor                    → Color_Mealtime_TT_Argb
                //   lblSME.ForeColor                       → Color_TT_Label_F_Argb
                // Falls back to a neutral gray + black text when the
                // facility hasn't customized (useColor off / ARGB=0).
                const showSmSme = selectedPatient.selectiveMenuMode === 1
                  && !!selectedPatient.selectiveMenuText;
                const bannerBg = colorConfig?.useColor
                  && colorConfig.labelBg
                  && colorConfig.labelBg !== '#000000'
                    ? colorConfig.labelBg : '#d4d4d4';
                const bannerFg = colorConfig?.useColor
                  && colorConfig.mealtimeTT
                  && colorConfig.mealtimeTT !== '#000000'
                    ? colorConfig.mealtimeTT : 'inherit';
                const smFg = colorConfig?.useColor
                  && colorConfig.labelFg
                  && colorConfig.labelFg !== '#000000'
                    ? colorConfig.labelFg : 'inherit';
                return (
                  <Box
                    sx={{
                      bgcolor: bannerBg,
                      color: bannerFg,
                      px: '10px',
                      py: '4px',
                      fontWeight: 700,
                      fontSize: '15px',
                      fontStyle: 'italic',
                      mb: '6px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {/* Tray line text — natural width, no flex-grow,
                        so the SME badge can sit immediately to its
                        right with a 50px gap. */}
                    <span style={{ flex: '0 0 auto' }}>{header}</span>
                    {showSmSme && (
                      <span
                        style={{
                          marginLeft: '50px',
                          fontFamily: '"Times New Roman", Times, serif',
                          fontStyle: 'normal',
                          fontWeight: 800,
                          fontSize: '19px',
                          // Same bg as the banner — matches WinForms
                          // where `lblSME.BackColor = lblHeader.BackColor`.
                          backgroundColor: bannerBg,
                          color: smFg,
                          padding: '0 4px',
                          lineHeight: 1.1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {selectedPatient.selectiveMenuText}
                      </span>
                    )}
                    {/* Mat-color cells — 2 slots at the very right
                        of the banner. WinForms `FillMatValues`
                        (.vb:3057) fill rules:
                          • both filled → cell1 = slot1, cell2 = slot2
                          • only slot1 filled → renders in cell 2
                            (rightmost) — single always right
                          • only slot2 filled → not shown
                            (WinForms `ElseIf` has no slot-2-only branch). */}
                    {(() => {
                      const hasA = !!(selectedPatient.matColor1_Name ?? '').trim();
                      const hasB = !!(selectedPatient.matColor2_Name ?? '').trim();
                      const cell1 = (hasA && hasB) ? {
                        name: selectedPatient.matColor1_Name,
                        bg: selectedPatient.matColor1_Back,
                        fg: selectedPatient.matColor1_Fore,
                      } : null;
                      const cell2 = (hasA && hasB) ? {
                        name: selectedPatient.matColor2_Name,
                        bg: selectedPatient.matColor2_Back,
                        fg: selectedPatient.matColor2_Fore,
                      } : (hasA ? {
                        name: selectedPatient.matColor1_Name,
                        bg: selectedPatient.matColor1_Back,
                        fg: selectedPatient.matColor1_Fore,
                      } : null);
                      if (!cell1 && !cell2) return null;
                      const cellSx: React.CSSProperties = {
                        // Cells sit flush against each other — the
                        // outer wrapper's `marginLeft: auto` handles
                        // pushing the group to the right edge of the
                        // banner.
                        marginLeft: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        alignSelf: 'stretch',
                        minWidth: '30px',
                        padding: '0 6px',
                        fontFamily: '"Times New Roman", Times, serif',
                        fontStyle: 'normal',
                        fontWeight: 800,
                        fontSize: '17px',
                        lineHeight: 1.1,
                        // Negative top/bottom margin eats the banner's
                        // `py: 4px` padding so the cell fills the
                        // banner's entire vertical height edge-to-edge.
                        marginTop: '-4px',
                        marginBottom: '-4px',
                      };
                      return (
                        <span style={{ marginLeft: 'auto', display: 'inline-flex', alignSelf: 'stretch' }}>
                          {cell1 && (
                            <span
                              style={{
                                ...cellSx,
                                backgroundColor: cell1.bg != null ? argbToHex(cell1.bg) : '#fff',
                                color: cell1.fg != null ? argbToHex(cell1.fg) : '#000',
                              }}
                            >
                              {cell1.name}
                            </span>
                          )}
                          {cell2 && (
                            <span
                              style={{
                                ...cellSx,
                                backgroundColor: cell2.bg != null ? argbToHex(cell2.bg) : '#fff',
                                color: cell2.fg != null ? argbToHex(cell2.fg) : '#000',
                              }}
                            >
                              {cell2.name}
                            </span>
                          )}
                        </span>
                      );
                    })()}
                  </Box>
                );
              })()}

              {/* Row: tray items on left, special-messages column
                  right next to them. Tray body uses `flex: 0 1 auto`
                  so it only takes the width it needs — messages
                  then sit right against the tray items rather than
                  being pushed to the far edge by a stretched
                  `flex: 1` column (matches the WinForms visualizer
                  where the messages hug the tray). */}
              <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                flexDirection: isNarrow ? 'column' : 'row',
                gap: isNarrow ? 1 : 0,
                // Sticky-footer scroll region. This is the ONLY
                // area inside DialogContent allowed to scroll —
                // grows to fill space between banners and footer,
                // scrolls internally when items exceed that space,
                // so the footer band stays pinned + visible.
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                // (The 28px right margin that used to sit here reserved
                // room for the vertical Notices tab. That tab is now a
                // drawer, so the ticket keeps the width.)
              }}>
                <Box sx={{
                  flex: '0 1 auto',
                  minWidth: 0,
                  maxWidth: isNarrow ? '100%' : '70%',
                  width: isNarrow ? '100%' : undefined,
                }}>
                  {detailQuery.isFetching || !selectedPatientDetail ? (
                    // Shimmer is visible for the ENTIRE duration
                    // the menu-generation call is fetching — using
                    // `isFetching` (not `isLoading`) so background
                    // refetches on cached patients still show the
                    // shimmer. `!selectedPatientDetail` covers the
                    // initial gap before the first data lands.
                    <MenuItemsShimmer />
                  ) : (
                    <TrayTicketVisualizerContent patient={selectedPatient} />
                  )}
                </Box>
                {/* Special-messages column. Mirrors the WinForms
                    `strSpecialMessage` block (Times New Roman 20pt,
                    facility `Color_TT_SpecialNeeds_Argb`). Server
                    builds the list via
                    `Diet_Utils.TrayTicketGetMessageText`.
                    Font sized at 24px so it visually reads as bold
                    and immediate — matches the WinForms 20pt draw. */}
                {selectedPatient.specialMessages && selectedPatient.specialMessages.length > 0 && (
                  <Box
                    sx={{
                      flex: '0 1 auto',
                      pl: 2,
                      fontFamily: '"Times New Roman", Times, serif',
                      fontSize: 28,
                      // Lighter weight — reads closer to the WinForms
                      // Times New Roman 20pt native draw (which
                      // GDI+ renders at a lighter apparent weight
                      // than a browser bolded serif).
                      fontWeight: 400,
                      lineHeight: 1.2,
                      textTransform: 'uppercase',
                      color: colorConfig?.useColor && colorConfig.specialNeeds && colorConfig.specialNeeds !== '#000000'
                        ? colorConfig.specialNeeds
                        : '#b91c1c',
                    }}
                  >
                    {selectedPatient.specialMessages.map((m: string, i: number) => (
                      <div key={i}>{m}</div>
                    ))}
                  </Box>
                )}
                {/* Selective-Menu Entered / Not Entered — LARGE
                    variant only (mode 2). Upper right of the body,
                    same Y as the messages. Mirrors WinForms
                    `lblSelectiveMenuNotEntered`. The small-badge
                    variant (mode 1 = `lblSME`) lives inside the
                    mealtime banner above, not here. */}
                {selectedPatient.selectiveMenuMode === 2 && selectedPatient.selectiveMenuText && (
                  <Box
                    sx={{
                      ml: 'auto',
                      alignSelf: 'flex-start',
                      // Offset left by 10px so the label clears
                      // the sticky-footer scrollbar in the tray
                      // body's `overflow: auto` region — otherwise
                      // the scrollbar clips the right edge of the
                      // text.
                      mr: '10px',
                      // WinForms designer sets `lblSelectiveMenuNotEntered`
                      // to Tahoma 12pt Italic, ForeColor = Red (hardcoded,
                      // NOT from a facility setting) — see
                      // frmDietech_Prd_TrayTicketVisualizer.Designer.vb:860-861.
                      fontFamily: 'Tahoma, sans-serif',
                      fontSize: '20px',
                      fontStyle: 'italic',
                      fontWeight: 400,
                      textAlign: 'right',
                      color: '#ff0000',
                    }}
                  >
                    {selectedPatient.selectiveMenuText}
                  </Box>
                )}
              </Box>

              {/* Footer band — TT note (left) + feeding-status +
                  resident photo (right), pinned to the very
                  lower-right of DialogContent via `mt: 'auto'` on
                  the wrapper so it stays at the bottom regardless
                  of how tall the item body is.
                  • TT_Note only appears for main mealtimes
                    (mealtime < 3, per WinForms .vb:2897).
                  • Colors:
                      Note        → colorConfig.notes         (Color_TT_Notes_Argb)
                      FeedingStatus → colorConfig.facilityType (Color_Facility_Type_Argb)
                  • Image visibility → facility
                    `TT_ThreePart_ShowResidentImage` (SetPatientImage
                    .vb:3391-3407). */}
              {(() => {
                // WinForms mapping: mealtimes 0/3/4→B, 1/5→L, 2/6→D.
                // Nourishments (7/8) don't map (mealtime < 3 gate).
                const ttNote = selectedMealtime < 3
                  ? (selectedMealtime === 1 ? selectedPatient.tT_Note_L
                    : selectedMealtime === 2 ? selectedPatient.tT_Note_D
                      : selectedPatient.tT_Note_B)
                  : '';
                const showNote = (ttNote ?? '').trim() !== '';
                const locationVal = selectedMealtime < 3
                  ? (selectedMealtime === 1 ? selectedPatient.location_L
                    : selectedMealtime === 2 ? selectedPatient.location_D
                      : selectedPatient.location_B)
                  : '';
                const showLocation = (locationVal ?? '').trim() !== '';
                // Image is served by GET /api/TrayTicket/PatientImage
                // — browser handles caching + lazy load. Endpoint
                // returns 404 when tblPatient.Image is NULL and the
                // <img>'s onError below hides the element cleanly.
                const showImage = !!facilitySettings?.tT_ThreePart_ShowResidentImage
                  && selectedPatientId != null;
                if (!showNote && !showLocation && !selectedPatient.feedingStatus && !showImage) return null;
                return (
                  <Box
                    sx={{
                      mt: 1,
                      pt: 0,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'flex-end',
                      gap: '50px',
                      // Reserve vertical space below the footer text
                      // for the absolutely-anchored patient photo
                      // (82px + 1px border + 8px inset = ~91px). The
                      // Note / Location / Feeding-Status labels now
                      // sit directly ABOVE the image instead of
                      // being overlapped by it.
                      pb: showImage ? '92px' : 0,
                    }}
                  >
                    {/* Note ("Note: {value}") — sits right beside the
                        feeding-status cell (no stretch), separated by
                        the 50px flex `gap`. */}
                    {showNote && (
                      <Box
                        sx={{
                          flex: '0 0 auto',
                          textAlign: 'left',
                          fontFamily: '"Times New Roman", Times, serif',
                          fontSize: '16px',
                          color: colorConfig?.useColor && colorConfig.notes && colorConfig.notes !== '#000000'
                            ? colorConfig.notes
                            : undefined,
                        }}
                      >
                        <span style={{ fontWeight: 700 }}>Note: </span>
                        {ttNote}
                      </Box>
                    )}
                    {/* Right cell — location + feeding status + photo.
                        Cell as a whole sits at the right of the
                        footer (via `justifyContent: 'flex-end'` on
                        the flex row) but its CONTENT is left-aligned
                        so the Location label and feeding-status label
                        share a common left edge. */}
                    <Box sx={{ textAlign: 'left', flex: '0 0 auto' }}>
                      {/* Location label — mainline meals only,
                          same mealtime mapping as Note. Mirrors
                          WinForms `lblLocationValue.Text =
                          rowPatient(BLD_Abbrev(m) + "_Location")`
                          (.vb:2924). Fore/background colors come
                          from facility `Color_TT_Location` /
                          `Color_TT_Location_Back` (.vb:1644-1647). */}
                      {(() => {
                        if (selectedMealtime >= 3) return null;
                        const loc = selectedMealtime === 1
                          ? selectedPatient.location_L
                          : selectedMealtime === 2
                            ? selectedPatient.location_D
                            : selectedPatient.location_B;
                        if (!(loc ?? '').trim()) return null;
                        const useLocColor = colorConfig?.useColor
                          && colorConfig.locationFg
                          && colorConfig.locationFg !== '#000000';
                        const useLocBg = colorConfig?.useColor
                          && colorConfig.locationBg
                          && colorConfig.locationBg !== '#000000';
                        return (
                          <Box
                            sx={{
                              fontFamily: '"Times New Roman", Times, serif',
                              fontSize: '16px',
                              display: 'inline-block',
                              // Zero left padding so the label aligns
                              // flush with the feeding-status label
                              // below it. Right padding only when a
                              // background chip is drawn.
                              pl: 0,
                              pr: useLocBg ? 0.5 : 0,
                              mb: 0.5,
                              color: useLocColor ? colorConfig!.locationFg : undefined,
                              backgroundColor: useLocBg ? colorConfig!.locationBg : undefined,
                            }}
                          >
                            <span style={{ fontWeight: 700 }}>Location: </span>
                            {loc}
                          </Box>
                        );
                      })()}

                      {selectedPatient.feedingStatus && (
                        <Box
                          sx={{
                            fontFamily: '"Times New Roman", Times, serif',
                            fontSize: '16px',
                            fontWeight: 700,
                            color: colorConfig?.useColor && colorConfig.facilityType && colorConfig.facilityType !== '#000000'
                              ? colorConfig.facilityType
                              : undefined,
                          }}
                        >
                          {selectedPatient.feedingStatus}
                        </Box>
                      )}
              {/* Patient photo now renders absolutely-positioned
                  at the very bottom-right of the DialogContent —
                  see below. Removed from the inline footer flow. */}
                    </Box>
                  </Box>
                );
              })()}
            </>
          )}

          {/* Patient photo — absolutely anchored to the very
              bottom-right of the DialogContent. Gated on
              `hasFreshDetail` so the <img> request doesn't fire
              until the menu-generation call returns — otherwise
              it races the shimmer + adds a stale request against
              the previously-viewed patient during a switch. */}
          {facilitySettings?.tT_ThreePart_ShowResidentImage
            && selectedPatientId != null
            && hasFreshDetail && (
            <Box sx={{
              position: 'absolute',
              right: 8,
              bottom: 8,
              zIndex: 1,
              pointerEvents: 'none',
            }}>
              <ResidentPhoto
                path={API.trayTicketPatientImage(facilityId, selectedPatientId)}
                alt={selectedPatient?.fullName ?? ''}
                style={{
                  maxWidth: 82,
                  maxHeight: 82,
                  objectFit: 'contain',
                  border: '1px solid var(--border-color, #d0d0d0)',
                  background: '#fff',
                }}
              />
            </Box>
          )}
        </DialogContent>
          </Box>
        </Box>
      </Dialog>

      {/* Explain Ticket — reuse existing dialog. */}
      {selectedPatient && (
        <TrayTicketExplainer
          open={explainerOpen}
          onClose={() => setExplainerOpen(false)}
          date={selectedDate}
          mealtime={selectedMealtime}
          fKey_Patient={selectedPatient.fKey_Patient}
          patientName={selectedPatient.fullName}
        />
      )}

      {/* Print Report options — mirrors WinForms
          `frmDietech_Report_Generator_TrayTicket` popup fired from
          the visualizer's Print click. Date + Number of Days,
          read-only Menu Cycle info, mealtime multi-select, then a
          Print action. Actual queue-submission wiring stays in
          `handlePrintWith` (widening to accept date range +
          mealtimes[] is the next step). */}
      <Dialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        // Lift above the visualizer Dialog which itself uses the
        // default modal z-index. Otherwise this popup renders
        // behind and can't be interacted with.
        sx={{ zIndex: (theme) => theme.zIndex.modal + 5 }}
      >
        <DialogTitle sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 1, px: 2,
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          Print Report...
          <IconButton
            size="small"
            onClick={() => setPrintDialogOpen(false)}
            sx={{ color: 'primary.contrastText' }}
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Date + Number of Days — the date mirrors the main
                visualizer toolbar exactly: prev-day chevron, 34px
                date field, next-day chevron. Number of Days sits to
                the right, same 34px height. Each takes its caption as
                its own label. */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <DateField
                  label="Date"
                  value={printDate}
                  onValueChanged={(v) => { if (v) setPrintDate(v); }}
                  displayFormat="M/D/YYYY"
                  width={217}
                  height={34}
                  showStepButtons
                />
                <NumberField
                  label="Number of Days"
                  value={printNumDays}
                  min={1}
                  decimals={0}
                  emptyValue={1}
                  width={140}
                  height={34}
                  showSpinButtons
                  onValueChanged={(v) => setPrintNumDays(Math.max(1, v ?? 1))}
                />
              </Box>
            </Box>

            {/* Menu Cycle info — refreshes as `printDate` changes.
                Content area has a fixed `minHeight` so switching
                between "loading / no cycle / real data" doesn't
                push the mealtime checkboxes below it up and down
                (layout bounce). Placeholder rendered during the
                fetch preserves the two-line footprint. */}
            <Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mb: 0.5,
                pb: 0.25,
                borderBottom: 1,
                borderColor: 'divider',
              }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Menu Cycle</Typography>
                {printMenuCycleQuery.isFetching && (
                  <CircularProgress size={10} thickness={5} />
                )}
              </Box>
              <Box sx={{ minHeight: 36 }}>
                {printMenuCycleDisplay?.menuCycleName ? (
                  <>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
                      {printMenuCycleDisplay.menuCycleName}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.5 }}>
                      {new Date(`${printDate}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' })}
                      {printMenuCycleDisplay.menuDay > 0 && <> {' | '} MC Day {printMenuCycleDisplay.menuDay}</>}
                    </Typography>
                  </>
                ) : printMenuCycleDisplay ? (
                  // We have a payload but it has no cycle name —
                  // show the "No active" fallback rather than a
                  // blank block.
                  <Typography sx={{ fontSize: 12, fontStyle: 'italic', color: 'text.disabled', lineHeight: 1.5 }}>
                    No active menu cycle
                  </Typography>
                ) : null}
              </Box>
            </Box>

            {/* Mealtime checkboxes — main meals only (Breakfast /
                Lunch / Supper). Header checkbox toggles all. */}
            <Box>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1, py: 0.5,
                bgcolor: 'grey.200',
                border: 1, borderColor: 'divider',
                borderTopLeftRadius: 4, borderTopRightRadius: 4,
              }}>
                <FormControlLabel
                  sx={{ mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={printMealtimes.length === 3}
                      indeterminate={printMealtimes.length > 0 && printMealtimes.length < 3}
                      onChange={handleToggleAllPrintMealtimes}
                    />
                  }
                  label={<Typography sx={{ fontSize: 13, fontWeight: 600 }}>Mealtimes</Typography>}
                />
              </Box>
              <Box sx={{
                border: 1, borderTop: 0, borderColor: 'divider',
                borderBottomLeftRadius: 4, borderBottomRightRadius: 4,
                px: 1, py: 0.5,
                display: 'flex', flexDirection: 'column',
              }}>
                {[0, 1, 2].map((slot) => {
                  const label = (facilityMealNames[slot] ?? '').trim() || MEALTIME_DEFAULTS[slot];
                  return (
                    <FormControlLabel
                      key={slot}
                      sx={{ mr: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={printMealtimes.includes(slot)}
                          onChange={() => handleTogglePrintMealtime(slot)}
                        />
                      }
                      label={<Typography sx={{ fontSize: 13 }}>{label}</Typography>}
                    />
                  );
                })}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        {/* Cancel, then Print (the primary) at the far right
            (docs/ui/dialog-actions.md). */}
        <DialogActionsBar
          onCancel={() => setPrintDialogOpen(false)}
          onSave={handlePrintSubmit}
          saveLabel="Print"
          saveDisabled={printMealtimes.length === 0 || printNumDays < 1}
          sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}
        />
      </Dialog>
    </>
  );
}

/** Compact ribbon-style grouping. When `label` is provided it
 *  renders under the controls (WinForms ribbon caption style);
 *  omit `label` to render only the control row with a divider. */
/**
 * Row-shimmer placeholder for the menu-items area — shown while
 * the per-patient menu-generation API call is in flight. Renders
 * as a stack of pulsating gray bars that roughly mimic the height
 * cadence of real item rows so the surrounding banners don't
 * shift when items land.
 */
function MenuItemsShimmer() {
  // Vary the widths so the shimmer reads as "text lines" instead
  // of a solid block. Keys are stable across renders.
  const widths = ['92%', '78%', '85%', '70%', '88%', '60%', '80%', '74%'];
  return (
    <Box sx={{ py: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {widths.map((w, i) => (
        <Box
          key={i}
          sx={{
            height: 18,
            width: w,
            borderRadius: '4px',
            bgcolor: 'grey.200',
            // Standard MUI Skeleton `pulse` keyframe rebuilt inline
            // so we don't need to add another import for a small
            // component. Slight per-row offset staggers the pulse
            // for visual interest.
            animation: 'pulse 1.6s ease-in-out infinite',
            animationDelay: `${i * 0.08}s`,
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.45 },
              '100%': { opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
  );
}

function RibbonGroup({
  label, children, sx, isNarrow,
}: { label?: string; children: React.ReactNode; sx?: object; isNarrow?: boolean }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        // Wide: vertical divider on the right, natural width.
        // Narrow (<sm, 600px): take full row width; vertical
        // divider disappears (the toolbar's rowGap already
        // separates wrapped groups). Add a bottom hairline
        // divider on narrow so stacked groups still read as
        // discrete sections.
        //
        // Explicit width/style/color triplets — MUI sx's shorthand
        // `border-*: {xs: 0, sm: 1}` doesn't always propagate the
        // `borderColor` alongside a responsive width, and the
        // browser falls back to text-color (near-black). Setting
        // each property individually forces the divider swatch to
        // apply.
        // Explicit boolean `isNarrow` (from useMediaQuery in the
        // parent) — plain JS ternaries so MUI's responsive-object
        // parsing never gets a chance to fail. Wide: vertical
        // divider on the right + natural width. Narrow: no
        // divider, full row width, extra vertical padding for a
        // stacked mobile-form feel.
        borderRightWidth: isNarrow ? 0 : '1px',
        borderRightStyle: 'solid',
        borderRightColor: 'divider',
        px: 1,
        pb: isNarrow ? 1 : 0.5,
        pt: isNarrow ? 0.5 : 0,
        width: isNarrow ? '100%' : 'auto',
        ...(sx ?? {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 44, flexWrap: 'wrap' }}>
        {children}
      </Box>
      {label && (
        <Typography
          sx={{
            fontSize: 10,
            color: 'text.disabled',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            textAlign: 'center',
            mt: 0.25,
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
}
